import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  setDoc,
  getDocs,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { placeholderFor } from './listingPlaceholder';
import { INTEREST_RESEND_AFTER_MS } from './listingInterestPolicy';
import { recordOfferIntentEvent, OFFER_INTENT_KINDS } from './offerIntentAnalytics';
import { trackWantAdded } from './effectAnalytics';
import { trackMissingPedal } from './pedalDatabase';
import { maybePromptForPush } from '../services/softPushPrompt';
import { offerModeOf } from './offerMode';

/**
 * The shared "quick want" core (WANT_DEMAND_BADGE_PLAN.md, 2026-09-16).
 *
 * Extracted from PedalDetails.jsx so the heart badge on listing cards and the
 * "Save to Wantlist" button on the listing page are the SAME code, not
 * hand-maintained mirrors. Behavior contract (Jason's call, 2026-08-23, kept
 * verbatim from the original):
 *
 *  - Adding a pedal to the Wantlist IS the interest signal. There is no
 *    standalone "I'm interested" action anywhere.
 *  - The want write is the user's primary intent. A failed interest nudge must
 *    never present itself as a failed add — sendInterestForListing never
 *    throws and never alerts.
 *  - The quick-add want is a "watch this pedal" bookmark: identity fields only
 *    (make/model/effectTypes); price/condition/decade/color stay null so the
 *    user can refine later via AddWantForm.
 *  - Removing the want withdraws the interest with it (best-effort): the two
 *    are one action, and a live interest on a de-listed want would tell the
 *    owner someone wants a pedal they just took off their list.
 */

/* ---------------------------------------------------------------- interest */

// localStorage tombstone: the last time THIS device sent an interest for a
// listing. The Firestore cooldown record is the listingInterest doc itself,
// but un-hearting deletes that doc — so without this, heart→unheart→heart
// re-signalled the owner on every cycle. Per-device politeness only; the
// adversarial defense is the server's 6h-push/24h-email throttle in
// functions/listingInterestNotifications.js (see the plan doc).
const interestTombstoneKey = (listingId) => `pxc_interest_sent_${listingId}`;

function markInterestSentLocally(listingId) {
  try {
    localStorage.setItem(interestTombstoneKey(listingId), String(Date.now()));
  } catch {
    // Private mode / blocked storage — the Firestore-side check still applies.
  }
}

function interestSentLocallyWithinCooldown(listingId) {
  try {
    const t = Number(localStorage.getItem(interestTombstoneKey(listingId)));
    return Boolean(t) && Date.now() - t < INTEREST_RESEND_AFTER_MS;
  } catch {
    return false;
  }
}

/**
 * Has this user already raised their hand on this listing, inside the resend
 * window? Mirrors the PedalDetails on-load check exactly: an existing doc
 * counts only within the 1h cooldown, and a round ended by a pass from either
 * side ('dismissed'/'closed') never blocks a resend.
 */
async function interestSentWithinCooldown(user, pedal) {
  if (interestSentLocallyWithinCooldown(pedal.id)) return true;
  const snap = await getDocs(query(
    collection(db, 'listingInterest'),
    where('fromUserId', '==', user.uid),
    where('listingId', '==', pedal.id)
  ));
  const existing = snap.docs[0]?.data();
  if (!existing) return false;
  const sentAt = existing.createdAt?.toDate?.();
  const withinCooldown =
    Boolean(sentAt) && Date.now() - sentAt.getTime() < INTEREST_RESEND_AFTER_MS;
  const roundEnded = existing.status === 'dismissed' || existing.status === 'closed';
  return withinCooldown && !roundEnded;
}

/**
 * Send the interest signal for a listing, if the sender is eligible.
 *
 * Returns 'sent' | 'skipped-no-listings' | 'skipped-already' | 'error'.
 * Never throws and never alerts (see the module contract above).
 *
 * @param {object} opts
 * @param {object} opts.user     Firebase Auth user (must be signed in)
 * @param {object} opts.pedal    listing data incl. id/ownerId
 * @param {boolean} [opts.alreadySent]  caller-known "already sent" state
 *   (PedalDetails passes its interestSent state when true). When undefined,
 *   the tombstone + Firestore cooldown check above runs instead — this is the
 *   card path, which holds no per-listing interest state.
 */
export async function sendInterestForListing({ user, pedal, alreadySent }) {
  if (!user || !pedal?.id || user.uid === pedal.ownerId) return 'skipped-already';
  // Sale-only listing (plan §5, Jason 2026-10-20): the want is still saved by
  // the caller, but "interested" is a trade signal and is not sent. The
  // listingInterest create rule refuses it as well.
  if (offerModeOf(pedal) === 'sell') return 'skipped-sale-only';
  try {
    const skip = alreadySent === undefined
      ? await interestSentWithinCooldown(user, pedal)
      : alreadySent;
    if (skip) return 'skipped-already';

    // Interest is a trade signal, and the owner's next step is to look at the
    // sender's pedalboard — from an empty one the signal goes nowhere. Checked
    // at tap time (same query the Account Checklist uses). The want still gets
    // written by the caller — a Wantlist is a private record and must not
    // require a listing; only the outbound nudge is withheld.
    const myListings = await getDocs(query(
      collection(db, 'listings'),
      where('ownerId', '==', user.uid),
      where('active', '==', true)
    ));
    // Only trade-eligible listings count: a sale-only pedal is never offered in a trade.
    if (!myListings.docs.some((d) => offerModeOf(d.data()) !== 'sell')) return 'skipped-no-listings';

    const interestRef = doc(db, 'listingInterest', `${pedal.id}_${user.uid}`);
    // A resend replaces the old round rather than editing it. The owner's
    // notification hangs off document creation, and overwriting in place is an
    // update in the eyes of the rules, which only the owner may do.
    await deleteDoc(interestRef).catch(() => {});
    await setDoc(interestRef, {
      listingId: pedal.id,
      listingOwnerId: pedal.ownerId,
      fromUserId: user.uid,
      status: 'open',
      createdAt: serverTimestamp(),
      respondedAt: null,
      // Snapshot so the owner's card still renders if the listing changes.
      listingSnapshot: {
        make: pedal.make || null,
        model: pedal.model || null,
        condition: pedal.condition || null,
        price: pedal.price ?? null,
        imageUrl: pedal.images?.top || null,
        placeholderUrl: pedal.images?.top ? null : placeholderFor(pedal).url
      }
    });
    markInterestSentLocally(pedal.id);
    void recordOfferIntentEvent(OFFER_INTENT_KINDS.LISTING_INTEREST_SENT, { listingId: pedal.id });
    return 'sent';
  } catch (err) {
    console.error('❌ Signalling interest failed:', err);
    return 'error';
  }
}

/* -------------------------------------------------------------- add/remove */

/** The identity-only want doc shape the quick add writes. */
export function buildQuickWantData(user, pedal) {
  const make = pedal.make || null;
  const model = pedal.model || null;
  const effectTypes = Array.isArray(pedal.effectTypes) ? pedal.effectTypes : [];
  return {
    uid: user.uid,        // legacy field; kept for backward compatibility
    ownerID: user.uid,    // required by Firestore rules on /wantList/{id}
    make,
    model,
    maxPrice: null,
    condition: null,
    decade: null,
    description: '',
    color: null,
    effectTypes,
    // Notify-on-match defaults match AddWantForm: true only when both make and
    // model are set (i.e. a specific pedal, not just an effect type).
    notifyOnMatch: !!(make && model)
  };
}

/**
 * Quick-add a listing to the user's Wantlist and send the interest signal.
 *
 * Throws if the want write fails (the caller owns the error UI). Everything
 * after the want write is best-effort and never throws.
 *
 * @param {object} opts
 * @param {object} opts.user   Firebase Auth user (must be signed in)
 * @param {object} opts.pedal  listing data
 * @param {boolean} [opts.alreadySentInterest]  see sendInterestForListing
 * @param {(wantId: string, wantData: object) => void} [opts.onWantWritten]
 *   invoked immediately after the want doc lands, before the (slower)
 *   interest/analytics tail — so the caller's UI can flip in the same beat.
 * @returns {Promise<{wantId: string, wantData: object, interestResult: string}>}
 */
export async function quickAddWantForListing({ user, pedal, alreadySentInterest, onWantWritten }) {
  const wantData = buildQuickWantData(user, pedal);
  const newWantRef = await addDoc(collection(db, 'wantList'), {
    ...wantData,
    createdAt: serverTimestamp()
  });
  try { sessionStorage.setItem('wantListMatches_dirty', '1'); } catch { /* private mode */ }
  if (onWantWritten) onWantWritten(newWantRef.id, wantData);

  // Adding this pedal to the Wantlist IS the interest signal now. Awaited so
  // a detail-page prompt line can flip to "we've let them know" in the same
  // beat as the button state rather than a visible instant later.
  const interestResult = await sendInterestForListing({
    user,
    pedal,
    alreadySent: alreadySentInterest
  });

  // Contextual soft-ask for push permission (native-only, no-op on web).
  // Mirrors AddWantForm: quick-add is still "adding a want" to the user.
  maybePromptForPush('add_want');

  // Analytics — fire-and-forget, identical pattern to AddWantForm.
  try {
    await trackWantAdded({
      id: newWantRef.id,
      make: wantData.make,
      model: wantData.model,
      maxPrice: null,
      effectTypes: wantData.effectTypes
    });
  } catch (analyticsErr) {
    console.error('❌ trackWantAdded failed (quick-add):', analyticsErr);
  }
  if (wantData.make && wantData.model) {
    try {
      await trackMissingPedal(wantData.make, wantData.model, user.uid);
    } catch (mpErr) {
      console.error('❌ trackMissingPedal failed (quick-add):', mpErr);
    }
  }

  return { wantId: newWantRef.id, wantData, interestResult };
}

/**
 * Quick-remove: delete the want and withdraw the interest with it.
 *
 * Throws if the want delete fails (caller owns the error UI). The interest
 * withdrawal is best-effort — a failed delete must not fail the removal, and
 * the interest expires on its own in 14 days regardless. The localStorage
 * tombstone is deliberately NOT cleared: re-adding inside the hour re-creates
 * the want but must not re-notify the owner.
 *
 * cleanupMatchesOnWantDelete (Cloud Function) tidies matches and
 * wantsMyPedals docs that referenced this wantId — no client work needed.
 *
 * @returns {Promise<{interestWithdrawn: boolean}>}
 */
export async function quickRemoveWantForListing({ user, pedal, wantId }) {
  await deleteDoc(doc(db, 'wantList', wantId));
  let interestWithdrawn = false;
  try {
    await deleteDoc(doc(db, 'listingInterest', `${pedal.id}_${user.uid}`));
    interestWithdrawn = true;
  } catch (interestErr) {
    console.error('❌ Could not withdraw interest with the want:', interestErr);
  }
  try { sessionStorage.setItem('wantListMatches_dirty', '1'); } catch { /* private mode */ }
  return { interestWithdrawn };
}

/* --------------------------------------------------------------- signed out */

/**
 * Signed-out behavior: navigate to /addpedal with the listing prefilled —
 * identical to the detail-page button today. The caller navigates (and
 * scrolls to top; React Router v6 does not auto-scroll).
 */
export function signedOutWantPrefillUrl(pedal) {
  const effectTypesParam = pedal.effectTypes && pedal.effectTypes.length > 0
    ? `&effectTypes=${encodeURIComponent(JSON.stringify(pedal.effectTypes))}`
    : '';
  return `/addpedal?tab=want&prefill=true&make=${encodeURIComponent(pedal.make || '')}&model=${encodeURIComponent(pedal.model || '')}&condition=${encodeURIComponent(pedal.condition || '')}&price=${encodeURIComponent(pedal.price || '')}${effectTypesParam}`;
}
